import { RatingData, PlayerData } from '../types/player';

// Helper function to safely get nested object value
export const getNestedValue = (obj: any, path: string): string | undefined => {
  if (!obj) return undefined;
  
  const parts = path.split('.');
  const sectionId = parts[0]; // e.g., 'family', 'financial', etc.
  const property = parts[1]; // e.g., 'summary'
  
  // Check if the section exists in the object
  if (obj[sectionId]) {
    // For the exact format provided, we need to access the summary property
    if (property === 'summary') {
      // If summary is a string, return it directly
      if (typeof obj[sectionId].summary === 'string') {
        return obj[sectionId].summary;
      }
      // If summary is an object with a summary property, return that
      else if (obj[sectionId].summary && typeof obj[sectionId].summary === 'object') {
        // Check for summary.summary (nested structure)
        if ('summary' in obj[sectionId].summary && typeof obj[sectionId].summary.summary === 'string') {
          return obj[sectionId].summary.summary;
        }
        // Also check for other properties that might contain text
        else if ('text' in obj[sectionId].summary && typeof obj[sectionId].summary.text === 'string') {
          return obj[sectionId].summary.text;
        }
        // If there's a rating explanation, that's also useful text
        else if (obj[sectionId].summary.rating && 
                typeof obj[sectionId].summary.rating === 'object' && 
                'explanation' in obj[sectionId].summary.rating && 
                typeof obj[sectionId].summary.rating.explanation === 'string') {
          return obj[sectionId].summary.rating.explanation;
        }
      }
    }
  }
  
  return undefined;
};

// Helper function to convert meters to feet and inches
export const metersToFeetAndInches = (heightStr: string | null | undefined): string => {
  if (!heightStr) {
    return 'N/A';
  }
  
  try {
    // First, determine if the height is in meters (like "1,90m") or centimeters (like "175")
    let heightInCm: number;
    
    // If it contains 'm', it's likely in meters format
    if (heightStr.includes('m')) {
      // Convert string like "1,90m" to number 1.90 by removing 'm' and replacing comma
      const meters = parseFloat(heightStr.replace('m', '').replace(',', '.'));
      if (isNaN(meters)) {
        return 'N/A';
      }
      heightInCm = meters * 100;
    } else {
      // Assume it's already in centimeters
      heightInCm = parseFloat(heightStr.replace(',', '.'));
      if (isNaN(heightInCm)) {
        return 'N/A';
      }
    }
    
    // Convert centimeters to inches
    const totalInches = heightInCm / 2.54;
    const feet = Math.floor(totalInches / 12);
    const inches = Math.round(totalInches % 12);
    
    return `${feet}'${inches}"`;
  } catch (error) {
    console.error('Error converting height:', error);
    return 'N/A';
  }
};

// Helper function to validate rating data
export const parseRating = (rating: RatingData | undefined | string | number): { rating: number; explanation: string } | null => {
  if (!rating) return null;
  
  try {
    // Handle if rating is already a number
    if (typeof rating === 'number') {
      return {
        rating: rating,
        explanation: 'No explanation provided'
      };
    }
    
    // Handle if rating is a string (could be a stringified number or JSON)
    if (typeof rating === 'string') {
      // Try to parse as JSON first
      try {
        const parsed = JSON.parse(rating);
        
        if (typeof parsed === 'object' && parsed !== null) {
          // Check if it has a nested score property (new format)
          if (parsed.score !== undefined) {
            return {
              rating: Number(parsed.score) || 0,
              explanation: parsed.explanation || 'No explanation provided'
            };
          }
          // Check if it has a nested rating property
          else if (parsed.rating !== undefined) {
            // Handle if rating is itself an object with score
            if (typeof parsed.rating === 'object' && parsed.rating !== null && parsed.rating.score !== undefined) {
              return {
                rating: Number(parsed.rating.score) || 0,
                explanation: parsed.explanation || parsed.rating.explanation || 'No explanation provided'
              };
            }
            return {
              rating: Number(parsed.rating) || 0,
              explanation: parsed.explanation || 'No explanation provided'
            };
          } else {
            return {
              rating: 0,
              explanation: 'No rating data found'
            };
          }
        } else if (typeof parsed === 'number') {
          // If it's just a number
          return {
            rating: parsed,
            explanation: 'No explanation provided'
          };
        } else {
          // If it's just a number as string
          return {
            rating: Number(rating) || 0,
            explanation: 'No explanation provided'
          };
        }
      } catch (e) {
        // If it's not valid JSON, treat as a plain number string
        return {
          rating: Number(rating) || 0,
          explanation: 'No explanation provided'
        };
      }
    }
    
    // Handle if rating is an object with the expected structure
    if (typeof rating === 'object' && rating !== null) {
      // Check if it has a nested score property (new format)
      if ('score' in rating) {
        return {
          rating: Number(rating.score) || 0,
          explanation: rating.explanation || 'No explanation provided'
        };
      }
      // Check if it has a nested rating property
      else if ('rating' in rating) {
        // Handle if rating is itself an object with score
        if (typeof rating.rating === 'object' && rating.rating !== null && 'score' in rating.rating) {
          return {
            rating: Number(rating.rating.score) || 0,
            explanation: rating.explanation || rating.rating.explanation || 'No explanation provided'
          };
        }
        
        return {
          rating: Number(rating.rating) || 0,
          explanation: rating.explanation || 'No explanation provided'
        };
      } else {
        return {
          rating: 0,
          explanation: 'No rating data found'
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error processing rating data:', error, rating);
    return null;
  }
};

// Check if player data has meaningful content
export const hasMeaningfulPlayerData = (playerData: PlayerData): boolean => {
  // Check if there are any article references
  const hasArticleReferences = playerData.article_references && 
    Object.keys(playerData.article_references).length > 0;
  
  // Check if there are any articles in the debug section
  const hasDebugArticles = playerData.debug && 
    playerData.debug.summary && 
    playerData.debug.summary.all_articles && 
    playerData.debug.summary.all_articles.length > 0;
  
  // Check if any section has a summary
  const hasSectionSummary = Object.keys(playerData).some(key => {
    const section = playerData[key as keyof PlayerData];
    if (typeof section === 'object' && section !== null && 'summary' in section) {
      const summary = typeof section.summary === 'string' 
        ? section.summary 
        : typeof section.summary === 'object' && section.summary !== null && 'summary' in section.summary
          ? section.summary.summary
          : '';
      
      // Only count non-empty summaries that don't contain generic text
      return summary && 
        summary.trim() !== '' && 
        !summary.includes('Information about The player') &&
        !summary.includes('is limited in the available sources');
    }
    return false;
  });
  
  // Check if any section has article references
  const hasSectionArticleReferences = Object.keys(playerData).some(key => {
    const section = playerData[key as keyof PlayerData];
    return typeof section === 'object' && 
      section !== null && 
      'article_references' in section && 
      section.article_references !== undefined &&
      Object.keys(section.article_references).length > 0;
  });
  
  return hasArticleReferences || hasDebugArticles || hasSectionSummary || hasSectionArticleReferences;
};